import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONTACT, SITE } from "@/data/content";

const easeEditorial = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. I'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Failed to send.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40 bg-white border-t border-black/10"
    >
      <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
        <div className="col-span-6 md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
            (04) — Contact
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 md:gap-12 lg:gap-16">
        {/* Massive heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: easeEditorial }}
          className="col-span-12 md:col-span-6"
        >
          <h2
            data-testid="contact-heading"
            className="font-serif font-light tracking-tighter leading-[0.95] text-[clamp(3rem,9vw,8rem)]"
          >
            Let's <span className="italic">work</span>
            <br />
            together<span className="italic">.</span>
          </h2>

          <p
            data-testid="contact-subtext"
            className="mt-10 font-serif text-xl md:text-2xl leading-snug text-neutral-700 max-w-[40ch]"
          >
            {CONTACT.subtext}
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">
                Email
              </p>
              <a
                data-testid="contact-email-link"
                href={`mailto:${SITE.email}`}
                className="font-serif italic text-2xl md:text-3xl link-underline"
              >
                {SITE.email}
              </a>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">
                Based in
              </p>
              <p className="font-serif text-2xl md:text-3xl">
                Florence, Italy — Working globally
              </p>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          data-testid="contact-form"
          onSubmit={onSubmit}
          noValidate
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.2, ease: easeEditorial }}
          className="col-span-12 md:col-span-6 md:pl-8 lg:pl-16"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-10">
            Send a note
          </p>

          <FieldRow label="Name" htmlFor="contact-name">
            <input
              id="contact-name"
              data-testid="contact-input-name"
              name="name"
              type="text"
              value={form.name}
              onChange={onChange}
              required
              autoComplete="name"
              className="w-full bg-transparent outline-none border-0 border-b border-black/30 focus:border-black py-3 font-serif text-xl md:text-2xl placeholder:text-neutral-400 transition-colors"
              placeholder="Your full name"
            />
          </FieldRow>

          <FieldRow label="Email" htmlFor="contact-email">
            <input
              id="contact-email"
              data-testid="contact-input-email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              autoComplete="email"
              className="w-full bg-transparent outline-none border-0 border-b border-black/30 focus:border-black py-3 font-serif text-xl md:text-2xl placeholder:text-neutral-400 transition-colors"
              placeholder="you@studio.com"
            />
          </FieldRow>

          <FieldRow label="Subject" htmlFor="contact-subject">
            <input
              id="contact-subject"
              data-testid="contact-input-subject"
              name="subject"
              type="text"
              value={form.subject}
              onChange={onChange}
              className="w-full bg-transparent outline-none border-0 border-b border-black/30 focus:border-black py-3 font-serif text-xl md:text-2xl placeholder:text-neutral-400 transition-colors"
              placeholder="Project, brand, or idea"
            />
          </FieldRow>

          <FieldRow label="Message" htmlFor="contact-message">
            <textarea
              id="contact-message"
              data-testid="contact-input-message"
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={4}
              className="w-full bg-transparent outline-none border-0 border-b border-black/30 focus:border-black py-3 font-serif text-xl md:text-2xl placeholder:text-neutral-400 resize-none transition-colors"
              placeholder="Tell me about the brand, the project, the moment."
            />
          </FieldRow>

          <div className="mt-10 flex items-center justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 max-w-[26ch]">
              By sending you agree to be contacted regarding your enquiry.
            </p>
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-black text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              <span>{loading ? "Sending…" : "Send message"}</span>
              <span
                aria-hidden
                className="inline-block transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function FieldRow({ label, htmlFor, children }) {
  return (
    <div className="mb-8">
      <label
        htmlFor={htmlFor}
        className="block text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
